import React, {useEffect, useRef, useState} from 'react';

//  simulating some system data


const orbitron = "'Orbitron', sans-serif";

const GALAXY_BG = `
    linear-gradient(rgba(0,0,0,0.92), rgba(0,0,0,0.95)),
    url('https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1920&q=80') center/cover no-repeat fixed
`;

const styles = {
    page: {
        minHeight: '100vh',
        background: GALAXY_BG,
        fontFamily: orbitron,
        position: 'relative',
        overflowX: 'hidden',
        paddingBottom: 64,
    },
    gridOverlay: {
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        backgroundImage: `linear-gradient(rgba(21,101,192,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(21,101,192,0.06) 1px,transparent 1px)`,
        backgroundSize: '48px 48px',
    },
    blob1: { position: 'fixed', width: 500, height: 500, borderRadius: '50%', background: 'rgba(21,101,192,0.1)', top: -150, left: -150, filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 },
    blob2: { position: 'fixed', width: 400, height: 400, borderRadius: '50%', background: 'rgba(255,215,0,0.04)', bottom: -100, right: -100, filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 },
};

const InfoRow = ({ label, value, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', gap: 12 }}>
        <span style={{ fontSize: '.48rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1.5px', flexShrink: 0 }}>{label}</span>
        <span style={{ fontSize: '.5rem', color: color || '#fff', fontWeight: 600, letterSpacing: '1px', textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
    </div>
);

const Panel = ({ title, children, noBorder }) => (
    <div style={{ padding: '24px 28px', borderRight: noBorder ? 'none' : '1px solid rgba(21,101,192,0.12)' }}>
        <div style={{ fontSize: '.5rem', color: '#1e88e5', letterSpacing: '3px', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid rgba(21,101,192,0.15)' }}>{title}</div>
        {children}
    </div>
);

const StatCard = ({ value, label, color }) => (
    <div style={{ background: 'rgba(21,101,192,0.07)', border: '1px solid rgba(21,101,192,0.18)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
        <div style={{ fontSize: '.75rem', fontWeight: 700, color: color || '#fff', letterSpacing: '1px', marginBottom: 4 }}>{value}</div>
        <div style={{ fontSize: '.4rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1.5px' }}>{label}</div>
    </div>
);

const SystemInfoLayout = ({ systemData, serverName }) => {
    const starsRef = useRef(null);

    const [diskSpace, setDiskSpace] = useState("11G");

    const data = systemData || {
        applicationVersion: "2.1.0-release",
        availableProcessors: 2,
        os: "Linux 6.8.0-85-generic",
        architecture: "amd64",
        jvmUptimeSeconds: 100272,
        totalBytesReceivedSinceBoot: 3785.12,
        totalBytesTransmittedSinceBoot: 3548.78,
        gpu: "Unknown (run lspci)",
        networkAdapter: "eth0",
        processor: "Intel Xeon Gold 5320 · 2.20GHz",
        diskSpace: "Total: 23G · Free: 11G",
        applicationName: "canal_api",
        machineCurrentTime: "2025-10-15 23:41:06 UTC",
        ram: "Total: 0.50 GB | Used: 0.13 GB",
    };

    useEffect(() => {
        if (!starsRef.current) return;
        const container = starsRef.current;
        container.innerHTML = '';
        if(data.diskSpace){
            const diskSpace = data.diskSpace.indexOf(" Free:");
            const diskSpaceValue = data.diskSpace.substring(diskSpace + 6, data.diskSpace.length - 1);
            setDiskSpace(diskSpaceValue);
        }

        for (let i = 0; i < 80; i++) {
            const star = document.createElement('div');
            const size = Math.random() * 2 + 1;
            const duration = 2 + Math.random() * 4;
            const delay = Math.random() * 4;
            star.style.cssText = `
                position:absolute;border-radius:50%;background:#fff;
                width:${size}px;height:${size}px;
                top:${Math.random() * 100}%;left:${Math.random() * 100}%;
                animation:twinkle ${duration}s ease-in-out ${delay}s infinite alternate;
            `;
            container.appendChild(star);
        }
    }, []);

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700;900&display=swap');
                @keyframes twinkle { 0%{opacity:.1} 100%{opacity:.8} }
                @keyframes pulse   { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.4);opacity:.6} }
                @keyframes scanline { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
                @keyframes fadeIn  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
                .sys-grid { display: grid; grid-template-columns: 1fr 1fr; }
                .sys-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
                @media (max-width: 600px) {
                    .sys-grid { grid-template-columns: 1fr !important; }
                    .sys-stats { grid-template-columns: repeat(2, 1fr) !important; }
                }
            `}</style>

            <div style={styles.page}>
                {/* Backgrounds */}
                <div style={styles.gridOverlay} />
                <div style={styles.blob1} />
                <div style={styles.blob2} />

                {/* Scanline */}
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg,transparent,rgba(21,101,192,0.3),transparent)', animation: 'scanline 8s linear infinite', zIndex: 1, pointerEvents: 'none' }} />

                {/* Stars */}
                <div ref={starsRef} style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }} />

                {/* Hero */}
                <div style={{ position: 'relative', zIndex: 5, textAlign: 'center', padding: '48px 24px 32px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid rgba(21,101,192,0.35)', borderRadius: 20, padding: '6px 16px', marginBottom: 24, background: 'rgba(21,101,192,0.08)' }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#1e88e5', animation: 'pulse 2s ease-in-out infinite' }} />
                        <span style={{ fontSize: '.5rem', color: '#1e88e5', letterSpacing: 3 }}>LIVE SYSTEM STATUS</span>
                    </div>
                    <h1 style={{ fontSize: 'clamp(1.2rem,3vw,2rem)', fontWeight: 900, color: '#fff', letterSpacing: 4, textTransform: 'uppercase', marginBottom: 10 }}>
                        System <span style={{ color: '#1e88e5' }}>Information</span>
                    </h1>
                    <p style={{ fontSize: '.55rem', color: 'rgba(255,255,255,0.35)', letterSpacing: 2, maxWidth: 440, margin: '0 auto', lineHeight: 1.9 }}>
                        Hardware &amp; runtime diagnostics for the Canal API deployment node
                    </p>
                </div>

                {/* Main Card */}
                <div style={{ position: 'relative', zIndex: 5, maxWidth: 900, margin: '0 auto', padding: '0 24px 48px', animation: 'fadeIn .6s ease both' }}>
                    <div style={{ background: 'rgba(8,8,24,0.92)', border: '1px solid rgba(21,101,192,0.35)', borderRadius: 20, overflow: 'hidden', backdropFilter: 'blur(16px)' }}>

                        {/* Top shimmer */}
                        <div style={{ height: 3, background: 'linear-gradient(90deg,transparent,#1e88e5 40%,#FFD700 60%,transparent)' }} />

                        {/* Card Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px', borderBottom: '1px solid rgba(21,101,192,0.15)' }}>
                            <span style={{ fontSize: '.65rem', fontWeight: 700, color: '#fff', letterSpacing: 3 }}>HARDWARE &amp; OPERATING SYSTEM</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.45rem', color: '#4caf50', letterSpacing: 2, border: '1px solid rgba(76,175,80,0.3)', padding: '4px 10px', borderRadius: 4, background: 'rgba(76,175,80,0.08)' }}>
                                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#4caf50', animation: 'pulse 2s ease-in-out infinite' }} />
                                ONLINE
                            </div>
                        </div>

                        {/* Row 1 */}
                        <div className="sys-grid">
                            <Panel title="APPLICATION">
                                <InfoRow label="APP NAME"    value={data.applicationName}     color="#1e88e5" />
                                <InfoRow label="VERSION"     value={data.applicationVersion}   color="#FFD700" />
                                <InfoRow label="FRAMEWORK"   value="React JS · Material-UI" />
                                <InfoRow label="CURRENT TIME" value={data.machineCurrentTime} />
                                <InfoRow label="JVM UPTIME"  value={`${data.jvmUptimeSeconds?.toLocaleString()} sec`} color="#4caf50" />
                            </Panel>
                            <Panel title="HARDWARE" noBorder>
                                <InfoRow label="OS"           value={data.os} />
                                <InfoRow label="ARCHITECTURE" value={data.architecture}        color="#1e88e5" />
                                <InfoRow label="PROCESSOR"    value={data.processor} />
                                <InfoRow label="RAM"          value={data.ram} />
                                <InfoRow label="DISK"         value={data.diskSpace}           color="#FFD700" />
                            </Panel>
                        </div>

                        {/* Row 2 */}
                        <div className="sys-grid" style={{ borderTop: '1px solid rgba(21,101,192,0.12)' }}>
                            <Panel title="NETWORK">
                                <InfoRow label="ADAPTER"   value={data.networkAdapter}                        color="#1e88e5" />
                                <InfoRow label="DOWNLOAD"  value={`${data.totalBytesReceivedSinceBoot?.toFixed(2)} MB`}    color="#4caf50" />
                                <InfoRow label="UPLOAD"    value={`${data.totalBytesTransmittedSinceBoot?.toFixed(2)} MB`} color="#4caf50" />
                            </Panel>
                            <Panel title="DISPLAY & CPU" noBorder>
                                <InfoRow label="GPU"        value={data.gpu} />
                                <InfoRow label="CPU CORES"  value={`${data.availableProcessors} processors`} color="#1e88e5" />
                                <InfoRow label="PROFILE"    value="Production" />
                            </Panel>
                        </div>

                        {/* Stat bar */}
                        <div className="sys-stats" style={{ padding: '20px 28px', borderTop: '1px solid rgba(21,101,192,0.12)' }}>
                            <StatCard value={data.applicationVersion} label="APP VERSION"  color="#1e88e5" />
                            <StatCard value="ONLINE"                  label="STATUS"       color="#4caf50" />
                            <StatCard value={`${diskSpace}`}                      label="DISK FREE"    color="#FFD700" />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{ position: 'relative', zIndex: 5, textAlign: 'center', padding: '0 24px' }}>
                    <p style={{ fontSize: '.45rem', color: 'rgba(255,255,255,0.15)', letterSpacing: 1.5 }}>
                        © 2026 BigG-Stream-tv · {serverName} · Data fetched: {new Date().toLocaleDateString()}
                    </p>
                </div>
            </div>
        </>
    );
};

export default SystemInfoLayout;